import { useState, useCallback, useMemo, useEffect } from "react";
import { findDeploymentForDate } from "../../../features/deployments";
import { clampDateToDeployment } from "./deployment-queries";
import { DeploymentNavStorage } from "./storage";
import { Deployment } from "../../../types/Deployment";

export function useDeploymentNavState(
    deployments: Deployment[],
    storage: DeploymentNavStorage,
    timezone: string,
) {
    // User-driven override (manual date selection)
    const [userSelectedDate, setUserSelectedDate] = useState<Date | null>(null);

    // Reset user selection when deployments change
    useEffect(() => {
        setUserSelectedDate(null);
    }, [deployments]);

    // Compute effective selectedDate and selectedDeployment
    const { selectedDate, selectedDeployment } = useMemo(() => {
        const today = new Date();
        const storedDate = storage.loadDate();
        let date: Date | null = null;
        let deployment: Deployment | null = null;
        if (deployments.length) {
            // 1. If user has selected a date and it's valid, use it
            if (userSelectedDate) {
                const d = findDeploymentForDate(
                    deployments,
                    userSelectedDate,
                    timezone,
                );
                if (d) {
                    date = clampDateToDeployment(userSelectedDate, d);
                    deployment = d;
                }
            }
            // 2. If not, use storedDate if valid
            if (!deployment && storedDate) {
                const d = findDeploymentForDate(
                    deployments,
                    storedDate,
                    timezone,
                );
                if (d) {
                    date = storedDate;
                    deployment = d;
                }
            }
            // 3. If not, use today if valid
            if (!deployment) {
                const d = findDeploymentForDate(deployments, today, timezone);
                if (d) {
                    date = today;
                    deployment = d;
                }
            }
            // 4. Fallback: use first day of latest deployment
            if (!deployment) {
                date = deployments[0].startTime;
                deployment = deployments[0];
            }
        }
        return { selectedDate: date, selectedDeployment: deployment };
    }, [deployments, userSelectedDate, storage]);

    // Persist to storage
    useEffect(() => {
        if (selectedDate) {
            storage.saveDate(selectedDate);
        }
    }, [selectedDate, storage]);

    // Handle date selection
    const setDateAndDeployment = useCallback((date: Date | null) => {
        setUserSelectedDate(date);
    }, []);

    const reset = useCallback(() => {
        setUserSelectedDate(null);
        storage.clear();
    }, [storage]);

    return {
        selectedDate,
        selectedDeployment,
        setDate: setDateAndDeployment,
        reset,
    };
}
